import { useCallback, useEffect, useState } from "react"

import { useCookie } from "../useCookie"
import { useLocation } from "react-router"

const FRAGMENT_ROUTE_KEY = "chat"
const FRAGMENT_ROUTE_REGEX = new RegExp(`#${FRAGMENT_ROUTE_KEY}=([^]+)`)
const FRAGMENT_ROUTE_COOKIE = "chatFragmentRoute"

// `useFragmentRouting` manages the Chat drawer's current route,
// which is tracked via the URL fragment, using the prefix `#chat=`.
//
// A route may contain query paramters using the familiar `?` + `&` syntax,
// which are valid fragment characters according to RFC 3987.
//
// Example:
// https://carlsbad.churchcenter.com/home?unrelated_query=1#chat=the/fragment/route?k=v&k2=v2
// produces a `route` of "the/fragment/route?k=v&k2=v2"
export function useFragmentRouting() {
  const { hash } = useLocation()
  const [lastOpenRoute, setLastOpenRoute] = useCookie(FRAGMENT_ROUTE_COOKIE)

  // `route` tracks the value of the `#chat=the/fragment/path` hash route
  const [route, _setRoute] = useState(extractChatFragmentRoute(hash))

  // updates route when location.hash changes
  useEffect(() => {
    function onHashChange() {
      // deliberately uses hash from window, not hook, so it can be called by event listener
      _setRoute(extractChatFragmentRoute(window.location.hash))
    }

    window.addEventListener("hashchange", onHashChange)
    return () => window.removeEventListener("hashchange", onHashChange)
  }, [])

  // updates route, location.hash, and lastOpenRoute
  const setRoute = useCallback(
    (path, params = {}) => {
      const queryString = new URLSearchParams(params).toString()
      const pathWithParams =
        path && queryString ? `${path}?${queryString}` : path
      const nextHash = pathWithParams
        ? `#${FRAGMENT_ROUTE_KEY}=${pathWithParams}`
        : "#"

      _setRoute(pathWithParams)
      history.replaceState(undefined, undefined, nextHash)
      if (pathWithParams !== null) setLastOpenRoute(pathWithParams)
    },
    [setLastOpenRoute, _setRoute],
  )

  return { lastOpenRoute, route, setRoute }
}

// '#chat=the/fragment/route?k=v' -> 'the/fragment/route?k=v'
// '#not-chat=something'          -> null
function extractChatFragmentRoute(hash) {
  const match = hash.match(FRAGMENT_ROUTE_REGEX)
  return match ? match[1] || "" : null
}
